import { useMemo, useState } from "react";

/**
 * SizeFinderStrip — pill-bar filter that lives at the top of /shop.
 *
 * Derives the visible pills from the actual inventory in the visitor's hands — so
 * we never show a "UK 22" pill when no piece ships in that size. Multi-select
 * within each row; OR-within / AND-across (so "Petite + UK 10" surfaces pieces that
 * are Petite AND sized UK 10).
 *
 * Props:
 *  - items: storefront item array (must include size_fit + size structured fields)
 *  - activeFits / activeSizes: Sets of currently-toggled pills
 *  - onChangeFits / onChangeSizes: setters that accept new Sets
 */
export default function SizeFinderStrip({ items, activeFits, activeSizes, onChangeFits, onChangeSizes }) {
  const { fits, sizes } = useMemo(() => {
    const fitSet = new Set();
    const sizeSet = new Set();
    for (const it of items || []) {
      if (it.size_fit && it.size_fit.toLowerCase() !== "regular") fitSet.add(it.size_fit);
      if (it.size) sizeSet.add(it.size);
    }
    return { fits: [...fitSet].sort(), sizes: [...sizeSet].sort() };
  }, [items]);

  const [showSizes, setShowSizes] = useState(false);

  if (fits.length === 0 && sizes.length === 0) return null;

  const toggle = (set, value, setter) => {
    const next = new Set(set);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    setter(next);
  };

  const clearAll = () => {
    onChangeFits(new Set());
    onChangeSizes(new Set());
    setShowSizes(false);
  };

  const totalActive = activeFits.size + activeSizes.size;

  // Limit the size row to ~12 by default; reveal the rest on demand so the bar stays elegant.
  const visibleSizes = showSizes ? sizes : sizes.slice(0, 12);

  return (
    <div
      className="max-w-7xl mx-auto px-8 lg:px-16 pt-8 pb-2 border-b border-taupe/30"
      data-testid="size-finder-strip"
    >
      <div className="flex items-center justify-between mb-3">
        <span className="editorial-label">size · find your fit</span>
        {totalActive > 0 && (
          <button
            onClick={clearAll}
            data-testid="size-finder-clear"
            className="text-[0.6rem] tracking-widest uppercase text-brass hover:text-espresso underline-offset-4 hover:underline"
          >
            clear ({totalActive})
          </button>
        )}
      </div>

      {fits.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="size-finder-fits">
          <span className="text-[0.6rem] tracking-widest uppercase text-taupe w-12 shrink-0">fit</span>
          {fits.map((f) => {
            const on = activeFits.has(f);
            return (
              <button
                key={f}
                onClick={() => toggle(activeFits, f, onChangeFits)}
                data-testid={`fit-pill-${f.toLowerCase()}`}
                className={`text-[0.7rem] tracking-widest uppercase rounded-full px-3.5 py-1.5 border transition-colors ${
                  on
                    ? "bg-charcoal text-ivory border-charcoal"
                    : "bg-transparent text-charcoal border-taupe hover:border-brass hover:text-brass"
                }`}
              >
                {f.toLowerCase()}
              </button>
            );
          })}
        </div>
      )}

      {sizes.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 pb-2" data-testid="size-finder-sizes">
          <span className="text-[0.6rem] tracking-widest uppercase text-taupe w-12 shrink-0">size</span>
          {visibleSizes.map((s) => {
            const on = activeSizes.has(s);
            return (
              <button
                key={s}
                onClick={() => toggle(activeSizes, s, onChangeSizes)}
                data-testid={`size-pill-${s.toLowerCase().replace(/\s+/g, "-")}`}
                className={`text-[0.7rem] tracking-widest rounded-full px-3.5 py-1.5 border transition-colors ${
                  on
                    ? "bg-brass text-ivory border-brass"
                    : "bg-transparent text-charcoal border-taupe hover:border-brass hover:text-brass"
                }`}
              >
                {s.toLowerCase()}
              </button>
            );
          })}
          {!showSizes && sizes.length > 12 && (
            <button
              onClick={() => setShowSizes(true)}
              data-testid="size-finder-more"
              className="text-[0.6rem] tracking-widest uppercase text-taupe hover:text-brass underline-offset-4 hover:underline"
            >
              + {sizes.length - 12} more
            </button>
          )}
        </div>
      )}
    </div>
  );
}
